import React, { useState, useMemo } from 'react';
import COMPETITORS_DATA from '../data/competitorsSeed';

const CLASS_STYLES = {
  direct: { color: '#dc2626', bg: '#fee2e2', label: 'Direct', dot: '#ef4444' },
  indirect: { color: '#2563eb', bg: '#dbeafe', label: 'Indirect', dot: '#3b82f6' },
  adjacent: { color: '#059669', bg: '#d1fae5', label: 'Adjacent', dot: '#22c55e' },
};

const MATRIX = [
  { name: 'ContexQ', breadth: 'High', speed: 'High', explain: 'High', domains: 'Multi-domain', apac: '★★★', cost: 'Low-Medium', pos: 'Agile Contextual Intelligence', highlight: true },
  { name: 'Palantir', breadth: 'High', speed: 'Low', explain: 'Medium', domains: 'Multi-domain', apac: '★☆☆', cost: 'Very High', pos: 'High-cost OS Platform' },
  { name: 'Quantexa', breadth: 'High', speed: 'Medium', explain: 'High', domains: 'Financial Crime', apac: '★★☆', cost: 'High', pos: 'Enterprise Financial Crime' },
  { name: 'SAS', breadth: 'Medium', speed: 'Low', explain: 'Medium', domains: 'Multi-domain', apac: '★★☆', cost: 'High', pos: 'Legacy Statistical Powerhouse' },
  { name: 'Neo4j', breadth: 'Low', speed: 'Medium', explain: 'Medium', domains: 'Graph DB', apac: '★☆☆', cost: 'Medium', pos: 'Graph Database Engine' },
  { name: 'TigerGraph', breadth: 'Low', speed: 'Medium', explain: 'Low', domains: 'Graph DB', apac: '★☆☆', cost: 'Medium', pos: 'Graph Analytics Engine' },
  { name: 'NICE Actimize', breadth: 'Medium', speed: 'Low', explain: 'Low', domains: 'Financial Crime', apac: '★★☆', cost: 'High', pos: 'Legacy AML/Fraud' },
  { name: 'Featurespace', breadth: 'Low', speed: 'Medium', explain: 'Medium', domains: 'Fraud', apac: '★☆☆', cost: 'Medium', pos: 'Adaptive ML Fraud' },
  { name: 'Feedzai', breadth: 'Low', speed: 'High', explain: 'Medium', domains: 'Fraud', apac: '★☆☆', cost: 'Medium', pos: 'Real-time Fraud AI' },
  { name: 'ComplyAdvantage', breadth: 'Low', speed: 'High', explain: 'Medium', domains: 'KYC/AML', apac: '★☆☆', cost: 'Low', pos: 'API-first AML Data' },
  { name: 'Informatica', breadth: 'Medium', speed: 'Low', explain: 'Low', domains: 'Data Mgmt', apac: '★★☆', cost: 'High', pos: 'Legacy MDM/DQ' },
  { name: 'Interos', breadth: 'Medium', speed: 'Medium', explain: 'Medium', domains: 'Supply Chain', apac: '★☆☆', cost: 'Medium', pos: 'Supply Chain Risk' },
  { name: 'Clarity AI', breadth: 'Low', speed: 'Medium', explain: 'High', domains: 'ESG', apac: '★☆☆', cost: 'Medium', pos: 'ESG Analytics' },
  { name: 'Senzing', breadth: 'Low', speed: 'High', explain: 'Medium', domains: 'Entity Resolution', apac: '★☆☆', cost: 'Low', pos: 'Embedded ER Engine' },
];

function CompetitiveIntel() {
  const [search, setSearch] = useState('');
  const [fClass, setFClass] = useState('');
  const [fCategory, setFCategory] = useState('');
  const [expandedCards, setExpandedCards] = useState({});

  const categories = useMemo(() => [...new Set(COMPETITORS_DATA.map(c => c.category))], []);

  const filtered = useMemo(() => {
    return COMPETITORS_DATA.filter(c => {
      if (fClass && c.classification !== fClass) return false;
      if (fCategory && c.category !== fCategory) return false;
      if (search) {
        const s = search.toLowerCase();
        return (c.name.toLowerCase().includes(s) || c.description.toLowerCase().includes(s) ||
          c.useCases.toLowerCase().includes(s) || c.strengths.toLowerCase().includes(s) ||
          c.weaknesses.toLowerCase().includes(s) || c.positioning.toLowerCase().includes(s));
      }
      return true;
    });
  }, [search, fClass, fCategory]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach(c => { if (!g[c.category]) g[c.category] = []; g[c.category].push(c); });
    return g;
  }, [filtered]);

  const toggleCard = (id) => setExpandedCards(prev => ({ ...prev, [id]: !prev[id] }));

  const direct = COMPETITORS_DATA.filter(c => c.classification === 'direct').length;
  const indirect = COMPETITORS_DATA.filter(c => c.classification === 'indirect').length;
  const adjacent = COMPETITORS_DATA.filter(c => c.classification === 'adjacent').length;

  const detailSection = (label, content) => {
    if (!content) return null;
    return (
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{content.replace(/\\n/g, '\n')}</div>
      </div>
    );
  };

  return (
    <div>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #0e4560 100%)', borderRadius: 14, padding: '32px 40px', marginBottom: 24, color: '#fff' }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, marginBottom: 6 }}>Competitive Intelligence Report 2026</h1>
        <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 20, maxWidth: 650 }}>Comprehensive battlecards covering {COMPETITORS_DATA.length} competitors across {categories.length} categories.</p>
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
          <div><div style={{ fontSize: 32, fontWeight: 900, color: '#38bdf8' }}>{COMPETITORS_DATA.length}</div><div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: .8 }}>Competitors</div></div>
          <div><div style={{ fontSize: 32, fontWeight: 900, color: '#38bdf8' }}>{categories.length}</div><div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase' }}>Categories</div></div>
          <div><div style={{ fontSize: 32, fontWeight: 900, color: '#f87171' }}>{direct}</div><div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase' }}>Direct</div></div>
          <div><div style={{ fontSize: 32, fontWeight: 900, color: '#93c5fd' }}>{indirect}</div><div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase' }}>Indirect</div></div>
          <div><div style={{ fontSize: 32, fontWeight: 900, color: '#86efac' }}>{adjacent}</div><div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase' }}>Adjacent</div></div>
        </div>
      </div>

      {/* Legend */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 20px', marginBottom: 20, display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center', fontSize: 13 }}>
        <strong>Classification:</strong>
        {Object.entries(CLASS_STYLES).map(([key, val]) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <div style={{ width: 11, height: 11, borderRadius: '50%', background: val.dot }} />
            <strong>{val.label}:</strong> {key === 'direct' ? 'Head-to-head in deals' : key === 'indirect' ? 'Overlapping use cases' : 'Complementary — integrates alongside'}
          </div>
        ))}
      </div>

      {/* Positioning Matrix */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '24px 28px', marginBottom: 28 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 16 }}>Competitive Positioning Matrix — ContexQ vs Key Players</h2>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead><tr>
            {['Company', 'Breadth', 'Speed', 'Explainability', 'Domains', 'APAC', 'Cost', 'Positioning'].map(h => (
              <th key={h} style={{ background: '#f1f5f9', padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0' }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>{MATRIX.map(r => (
            <tr key={r.name} style={{ background: r.highlight ? '#eff6ff' : 'transparent' }}>
              <td style={{ padding: '8px 10px', fontWeight: r.highlight ? 800 : 600, color: r.highlight ? '#1d4ed8' : '#1e293b', fontSize: r.highlight ? 13 : 12, borderBottom: '1px solid #f1f5f9' }}>{r.name}</td>
              <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9' }}>{r.breadth}</td>
              <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9' }}>{r.speed}</td>
              <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9' }}>{r.explain}</td>
              <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9' }}>{r.domains}</td>
              <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9' }}>{r.apac}</td>
              <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9' }}>{r.cost}</td>
              <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9', fontStyle: 'italic', color: '#64748b' }}>{r.pos}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>

      {/* Search + Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 300px', display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0 12px' }}>
          <span>🔍</span>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search competitors..." style={{ border: 'none', background: 'none', padding: '10px 0', fontSize: 13, outline: 'none', width: '100%' }} />
        </div>
        <select value={fClass} onChange={e => setFClass(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, fontWeight: 600, background: '#fff' }}>
          <option value="">All Classifications</option>
          <option value="direct">Direct</option>
          <option value="indirect">Indirect</option>
          <option value="adjacent">Adjacent</option>
        </select>
        <select value={fCategory} onChange={e => setFCategory(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, fontWeight: 600, background: '#fff' }}>
          <option value="">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Results count */}
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>Showing {filtered.length} of {COMPETITORS_DATA.length} competitors</div>

      {/* Category sections */}
      {Object.entries(grouped).map(([category, comps]) => (
        <div key={category} style={{ marginBottom: 40 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, paddingBottom: 10, borderBottom: '2px solid #e2e8f0' }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>{category}</h2>
            <span style={{ background: '#f1f5f9', color: '#64748b', fontSize: 12, padding: '3px 12px', borderRadius: 20, fontWeight: 600 }}>{comps.length}</span>
          </div>

          {comps.map(c => {
            const cls = CLASS_STYLES[c.classification] || CLASS_STYLES.direct;
            const isExpanded = expandedCards[c.id];

            return (
              <div key={c.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, marginBottom: 12, boxShadow: '0 1px 3px rgba(0,0,0,.06)', overflow: 'hidden' }}>
                {/* Card header */}
                <div onClick={() => toggleCard(c.id)} style={{ padding: '16px 20px', cursor: 'pointer' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div>
                      <div style={{ fontSize: 17, fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>{c.name}</div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, color: cls.color, background: cls.bg }}>● {cls.label}</span>
                        <span style={{ fontSize: 11, color: '#64748b' }}>{c.category}</span>
                        {c.website && <span style={{ fontSize: 11, color: '#2563eb' }}>🌐 {c.website}</span>}
                      </div>
                    </div>
                    <span style={{ fontSize: 14, color: '#9ca3af' }}>{isExpanded ? '▼' : '▶'}</span>
                  </div>
                  {c.description && <p style={{ fontSize: 12, color: '#64748b', lineHeight: 1.6, marginTop: 4 }}>{c.description.substring(0, 200)}{c.description.length > 200 ? '...' : ''}</p>}
                  <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 11, color: '#94a3b8' }}>
                    {c.founded && c.founded !== '—' && <span>Founded: {c.founded}</span>}
                    {c.hq && c.hq !== '—' && <span>HQ: {c.hq}</span>}
                    {c.funding && c.funding !== 'N/A' && c.funding !== '—' && <span>Funding: {c.funding}</span>}
                    {c.employees && c.employees !== '—' && <span>Employees: {c.employees}</span>}
                  </div>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div style={{ padding: '0 20px 20px', borderTop: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginTop: 16 }}>
                      <div>
                        {detailSection('🎯 Use Cases', c.useCases)}
                        {detailSection('🏭 Sector Focus', c.sectorFocus)}
                        {detailSection('🗺️ Regions', c.regions)}
                        {detailSection('✅ Strengths', c.strengths)}
                        {detailSection('⚠️ Weaknesses', c.weaknesses)}
                      </div>
                      <div>
                        {detailSection('🔥 Customer Pain Points', c.painPoints)}
                        {detailSection('🏗️ Architecture Limitations', c.archLimitations)}
                        {detailSection('🎯 ContexQ Positioning', c.positioning)}
                        {detailSection('🏆 Win Themes', c.winThemes)}
                        {detailSection('💬 Objection Handling', c.objectionHandling)}
                      </div>
                    </div>
                    {c.techStack && (
                      <div style={{ marginTop: 12, background: '#f8fafc', borderRadius: 8, padding: 12 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 4 }}>⚙️ Technology Stack</div>
                        <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{c.techStack.replace(/\\n/g, '\n')}</div>
                      </div>
                    )}
                    {c.pricing && (
                      <div style={{ marginTop: 8, fontSize: 11, color: '#64748b' }}>💰 Pricing: {c.pricing}</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export default CompetitiveIntel;
